import { useTranslation } from 'react-i18next';
import type { MobilePlanRow } from '../../pages/mobile/mobileApi';

type Props = {
  row: MobilePlanRow;
  variant: 'running' | 'next' | 'upcoming' | 'done';
  onStart?: (row: MobilePlanRow) => void;
  onEnd?: (row: MobilePlanRow) => void;
};

function formatTime(iso: string | undefined, locale: string): string {
  if (!iso) {
    return '–';
  }
  return new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

function plannedDelayMinutes(row: MobilePlanRow): number {
  if (!row.plannedStart || !row.actualStart) {
    return 0;
  }
  const planned = new Date(row.plannedStart).getTime();
  const actual = new Date(row.actualStart).getTime();
  return Math.round((actual - planned) / 60000);
}

const wrapperByVariant: Record<Props['variant'], string> = {
  running: 'border-[var(--pb-primary)] bg-[rgba(var(--pb-primary-rgb),0.08)]',
  next: 'border-gray-300 bg-white',
  upcoming: 'border-gray-200 bg-gray-50 text-gray-600',
  done: 'border-gray-200 bg-gray-100 text-gray-500',
};

export default function OperationCard({ row, variant, onStart, onEnd }: Props) {
  const { t, i18n } = useTranslation(['common']);
  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const delay = plannedDelayMinutes(row);

  return (
    <div
      className={`rounded-xl border p-4 mb-3 ${wrapperByVariant[variant]}`}
    >
      <div className="flex items-baseline justify-between gap-3">
        <div className="font-semibold text-base truncate">
          {row.productName ?? row.operationName ?? row.executionUuid}
        </div>
        {row.orderNumber && (
          <div className="text-xs text-gray-500 shrink-0">#{row.orderNumber}</div>
        )}
      </div>

      <div className="text-sm mt-1">
        {t('mobile.operation.plannedRange', {
          from: formatTime(row.plannedStart, locale),
          to: formatTime(row.plannedEnd, locale),
        })}
      </div>

      {row.actualStart && (
        <div className="text-sm">
          {t('mobile.operation.actualStart', { time: formatTime(row.actualStart, locale) })}
          {delay > 0 && (
            <span className="ml-2 text-orange-600">
              ({t('mobile.operation.delayed', { minutes: delay })})
            </span>
          )}
        </div>
      )}

      {row.actualEnd && (
        <div className="text-sm">
          {t('mobile.operation.actualEnd', { time: formatTime(row.actualEnd, locale) })}
        </div>
      )}

      {variant === 'running' && onEnd && (
        <button
          type="button"
          onClick={() => onEnd(row)}
          className="mt-3 w-full rounded-lg bg-[var(--pb-primary)] px-4 py-3 text-base font-semibold text-white active:opacity-90"
        >
          {t('mobile.record.endNow')}
        </button>
      )}

      {variant === 'next' && onStart && (
        <button
          type="button"
          onClick={() => onStart(row)}
          className="mt-3 w-full rounded-lg bg-[var(--pb-primary)] px-4 py-3 text-base font-semibold text-white active:opacity-90"
        >
          {t('mobile.record.startNow')}
        </button>
      )}
    </div>
  );
}
